import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TbAlertCircle, TbMail, TbPhone, TbUser, TbX } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { useAddRestaurantMutation } from '@/api/hooks/useRestaurant';
import { AddRestaurantParams } from '@/api/lib/restaurant';

import deliverooLogo from '../../../assets/images/deliveroo.png';
import justEatlogo from '../../../assets/images/just-eat.png';
import customDeliveryLogo from '../../../assets/images/other-delivery.png';
import uberEatlogo from '../../../assets/images/uber-eat.png';
import { WriteSwornDeclarationForm } from '../WriteSwornDeclarationForm';
import { Spinner } from '@/components/atoms/Spinner';
import { Typography } from '@/components/atoms/Typography';
import { RadioGroup } from '@/components/forms/base/RadioGroup';
import { TextInput } from '@/components/forms/base/TextInput';
import { Form } from '@/components/forms/core/Form';
import { AutoCompleteRestaurantInput } from '@/components/forms/custom/AutocompleteRestaurantInput';
import { AutoCompleteTagInput } from '@/components/forms/custom/AutocompleteTagInput';
import { Button } from '@/components/molecules/Button';

import { AddRestaurantSchemaType, validationSchema } from './schema';
import { MiniTitleWrapper } from '@/components/atoms/MiniTitleWrapper';

export interface AddRestaurantFormProps {
  initialData?: AddRestaurantSchemaType;
  restaurant?: AddRestaurantParams;
}

export const AddRestaurantForm = ({ initialData, restaurant }: AddRestaurantFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [alreadyRegisteredRestaurantId, setAlreadyRegisteredRestaurantId] = React.useState<string | null>(null);

  const { isLoading: isAddingRestaurant, mutateAsync: addRestaurantMutation, isError: isAddingError } = useAddRestaurantMutation({});

  const handleSubmit = async (data: AddRestaurantParams) => {
    if (data.isPlaceAlreadyRegistered) {
      toast.error(t('proposeRestaurant.restaurantOwnershipMessage'), {
        position: 'bottom-right'
      });
    } else {
      await addRestaurantMutation({
        ...data,
        id: restaurant?.id ?? uuid()
      });
      navigate(`/restaurant/inactive?${data.autocompleteName}`);
    }
  };

  if (isAddingError) {
    return (
      <div className="flex  flex-col justify-center items-top w-full gap-10 h-full">
        <div className="border p-10 w-fit mx-auto rounded-3xl bg-gray-50 border-red-400">
          <TbX className="text-red-700 bg-white text-7xl mx-auto border rounded-full p-2 mb-3 border-red-400" />
          <Typography weight="semibold" color="warning" align="center" variant="h3">
            Une erreur s'est produite. Veuillez réessayer et nous contacter en cas d'erreur.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="gap-4 flex flex-col w-full ">
      <Form
        data-testid="restaurant-form"
        schema={validationSchema(t)}
        onSubmit={handleSubmit}
        onlyTouchedFields={false}
        defaultValues={
          initialData && {
            ...initialData,
            alcohol: initialData.alcohol,
            tagIds: initialData.tagIds?.filter((tag): tag is string => tag !== null && tag !== undefined),
            suppliers: initialData.suppliers?.filter((supplier): supplier is string => supplier !== null && supplier !== undefined),
            company: initialData.company
          }
        }
        className="flex flex-col gap-5 "
        resetOnSubmit={!initialData}
        render={({ formState, watch, setValue }) => {
          const restaurantName = watch('autocompleteName');
          const restaurantAddress = restaurantName ? restaurantName.split(',')[0] : '';
          const firstName = watch('firstName');
          const lastName = watch('lastName');

          React.useEffect(() => {
            if (firstName) {
              setValue('firstName', firstName);
            }
            if (lastName) {
              setValue('lastName', lastName);
            }
            if (restaurantName) {
              setValue('restaurantName', restaurantName);
            }
            if (restaurantAddress) {
              setValue('address', restaurantAddress);
            }
          }, [restaurantName, restaurantAddress, firstName, lastName, setValue]);

          return (
            <>
              {!alreadyRegisteredRestaurantId ? (
                <>
                  {!isAddingRestaurant ? (
                    <>
                      <AutoCompleteRestaurantInput
                        searchInputId="autocompleteName"
                        searchPlaceId="placeId"
                        label={t('form.restaurantAndCity')}
                        weight="bold"
                        disabled={!!initialData?.autocompleteName}
                        placeholder={t('form.restaurantAndCityPlaceholder')}
                        onSelectSuggestion={(suggestion, restaurantId) => {
                          setAlreadyRegisteredRestaurantId((suggestion.displayedText ?? '')?.includes('✅') ? restaurantId : null);
                        }}
                        required
                      />
                      {initialData?.autocompleteName && (
                        <div className="text-gray-500 -mt-5 text-sm">La proposition de fiche en cours est uniquement applicable au restaurant sélectionné.</div>
                      )}

                      <AutoCompleteTagInput
                        searchTagInputId="autocompleteTag"
                        searchTagInputIds="tagIds"
                        label={t('form.specialties')}
                        weight="bold"
                        placeholder={t('form.restaurantSpecialties')}
                        required
                      />

                      <div className="flex flex-row gap-5 md:gap-12">
                        <div className="flex flex-1 flex-col gap-5">
                          <TextInput id="uberEatsLink" logo={uberEatlogo} label={t('form.uberEatsLink')} placeholder={t('proposeRestaurant.deliveryLink')} />
                          <TextInput
                            id="deliverooLink"
                            logo={deliverooLogo}
                            label={t('form.deliverooLink')}
                            placeholder={t('proposeRestaurant.deliveryLink')}
                          />
                        </div>
                        <div className="flex flex-1 flex-col gap-5">
                          <TextInput id="justEatLink" logo={justEatlogo} label={t('form.justEatLink')} placeholder={t('proposeRestaurant.deliveryLink')} />
                          <TextInput
                            id="customDeliveryLink"
                            logo={customDeliveryLogo}
                            label={t('form.customDeliveryLink')}
                            placeholder={t('proposeRestaurant.deliveryLink')}
                          />
                        </div>
                      </div>
                      <RadioGroup
                        id="alcohol"
                        name="alcohol"
                        label={t('form.alcoholLabel')}
                        horizontal
                        required
                        options={[
                          { value: 'false', title: t('action.no') },
                          { value: 'true', title: t('action.yes') }
                        ]}
                      />

                      <MiniTitleWrapper title={t('form.restaurantInformation')} />

                      <div className="flex flex-1 flex-row gap-5 md:gap-12">
                        <div className="flex flex-1 flex-col gap-5">
                          <TextInput
                            id="phoneNumberOwner"
                            leftIcon={<TbPhone />}
                            label={t('form.yourPhone')}
                            placeholder={t('form.yourPhonePlaceholder')}
                            required
                          />
                        </div>
                        <div className="flex flex-1 flex-col gap-5">
                          <TextInput
                            id="emailOwner"
                            leftIcon={<TbMail />}
                            label={t('form.yourEmailAddress')}
                            placeholder={t('form.yourEmailAddressPlaceholder')}
                            disabled
                          />
                        </div>
                      </div>
                      <div className="flex flex-1 flex-row gap-5 md:gap-12">
                        <div className="flex flex-1 flex-col gap-5">
                          <TextInput id="firstName" leftIcon={<TbUser />} label={t('form.firstName')} placeholder={t('form.firstName')} required />
                        </div>
                        <div className="flex flex-1 flex-col gap-5">
                          <TextInput id="lastName" leftIcon={<TbUser />} label={t('form.lastName')} placeholder={t('form.lastName')} required />
                        </div>
                      </div>

                      <MiniTitleWrapper className="mt-4 md:mt-14 mb-2" title={t('form.declaration')} />

                      <WriteSwornDeclarationForm watch={watch} showRepresentativeRestaurantAddress={false} />

                      <div className="flex items-center gap-1 mt-5">
                        <input type="checkbox" id="acceptTerms" className="mr-2 rounded" required />
                        <Typography variant="extraSmall" color="gray">
                          {t('swornDeclaration.acceptTerms')}
                        </Typography>
                      </div>
                    </>
                  ) : (
                    <>
                      <Spinner big />
                      <Typography variant="h4" color="gray">
                        Envoi de vos données en cours. Veuillez patienter...
                      </Typography>
                    </>
                  )}
                  <Button type="submit" fullWidth color="primary" label="Envoyer" loading={isAddingError || isAddingRestaurant} disabled={!formState.isDirty} />
                </>
              ) : (
                <div className=" rounded-xl space-y-5">
                  <div className="flex gap-2 items-center justify-center">
                    <TbAlertCircle size={32} className="text-red-600" />
                    <Typography variant="h2" weight="semibold">
                      Restaurant déjà ajouté sur Mon-Resto-Halal.com
                    </Typography>
                  </div>

                  <Typography align="center">Si ce restaurant vous appartient, effectuez un transfert de propriétaire.</Typography>

                  <Button
                    fullWidth
                    color="success"
                    label="Récuperer mon restaurant"
                    onClick={() => {
                      navigate(`/claim/${alreadyRegisteredRestaurantId}`);
                    }}
                  />
                </div>
              )}
            </>
          );
        }}
      />
    </div>
  );
};
